// tippi/src/components/driver/CarrierOffersSection.js
import React, { useState } from 'react';
import { useDriverCarrierOffers } from '../../hooks/useDriverCarrierOffers';
import { carrierApi } from '../../firebase/functionsService';
import { orderTitle } from '../../utils/orderTitle';
import { placeName, toDate, dayLabel, timeLabel } from '../../utils/orderCardFormat';
import { COLORS, FONTS } from '../../styles/tokens';

function OfferCard({ offer }) {
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const when = toDate(offer.scheduledAt);

  async function respond(accept) {
    if (!accept && !reason.trim()) { setError('Skriv en kort begrunnelse'); return; }
    setBusy(true);
    setError('');
    try {
      await carrierApi.respondToCarrierOrder({ orderId: offer.id, accept, ...(accept ? {} : { reason }) });
      // Tilbudet forsvinner fra listen når Firestore melder om den nye statusen
    } catch (err) {
      setError(err.message || 'Kunne ikke sende svar');
      setBusy(false);
    }
  }

  return (
    <div style={styles.card}>
      {when && <div style={styles.meta}>{`${dayLabel(when)} ${timeLabel(when)}`}</div>}
      <div style={styles.title}>{orderTitle(offer)}</div>
      <div style={styles.sub}>{placeName(offer.pickupAddress)} → {placeName(offer.deliveryAddress)}</div>
      {offer.customer?.name && <div style={styles.sub}>Kunde: {offer.customer.name}</div>}
      {offer.dispatcherNote && <div style={styles.note}>Notat fra dispatcher: {offer.dispatcherNote}</div>}

      {declining ? (
        <div style={{ marginTop: 12 }}>
          <label htmlFor={`d-${offer.id}`} style={styles.label}>Begrunnelse for avslag</label>
          <input id={`d-${offer.id}`} style={styles.input} value={reason} onChange={e => setReason(e.target.value)} maxLength={300} />
          {error && <p role="alert" style={styles.error}>{error}</p>}
          <div style={styles.row}>
            <button type="button" style={styles.btnOutline} disabled={busy} onClick={() => respond(false)}>Send avslag</button>
            <button type="button" style={styles.btnPlain} disabled={busy} onClick={() => { setDeclining(false); setError(''); }}>Avbryt</button>
          </div>
        </div>
      ) : (
        <>
          {error && <p role="alert" style={styles.error}>{error}</p>}
          <div style={styles.row}>
            <button type="button" className="tippi-btn" style={styles.btnDark} disabled={busy} onClick={() => respond(true)}>Godta</button>
            <button type="button" style={styles.btnOutline} disabled={busy} onClick={() => setDeclining(true)}>Avslå</button>
          </div>
        </>
      )}
    </div>
  );
}

// «Forespørsler fra firmaet» øverst i Mine oppdrag: egne ordre dispatcheren har tilbudt sjåføren.
export default function CarrierOffersSection() {
  const { offers } = useDriverCarrierOffers();
  if (offers.length === 0) return null;
  return (
    <section aria-label="Forespørsler fra firmaet" style={{ marginBottom: 8 }}>
      <h2 style={styles.heading}>Forespørsler fra firmaet</h2>
      {offers.map(o => <OfferCard key={o.id} offer={o} />)}
    </section>
  );
}

const styles = {
  heading: { fontFamily: FONTS.display, fontSize: 22, fontWeight: 700, color: COLORS.textDark, margin: '8px 0' },
  card:    { background: COLORS.white, borderBottom: `1px solid ${COLORS.border}`, padding: '14px 0' },
  meta:    { fontFamily: 'var(--font-mono)', fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: 0.3 },
  title:   { fontFamily: FONTS.display, fontSize: 28, lineHeight: 1.05, fontWeight: 700, color: COLORS.textDark, margin: '2px 0' },
  sub:     { fontSize: 14, color: COLORS.textMid },
  note:    { fontSize: 13.5, color: COLORS.textDark, background: COLORS.warningBg, borderRadius: 10, padding: '8px 10px', marginTop: 8 },
  label:   { display: 'block', fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 5 },
  input:   { width: '100%', boxSizing: 'border-box', height: 44, padding: '0 12px', borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 15, fontFamily: 'inherit' },
  row:     { display: 'flex', gap: 10, marginTop: 12 },
  btnDark:    { flex: 1, height: 48, borderRadius: 24, border: 'none', background: COLORS.primary, color: COLORS.onPrimary, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  btnOutline: { flex: 1, height: 48, borderRadius: 24, border: `2px solid ${COLORS.primary}`, background: COLORS.white, color: COLORS.textDark, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  btnPlain:   { height: 48, padding: '0 16px', borderRadius: 24, border: 'none', background: 'none', color: COLORS.textMid, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  error:   { background: COLORS.dangerBg, color: COLORS.dangerText, padding: '8px 10px', borderRadius: 10, fontSize: 13.5, margin: '10px 0 0' },
};
